// Background + manual dataset refresh.
//
// WHY THIS EXISTS: DataGate hydrates the store once per page load and used to
// never fetch again — no polling, no invalidation, no refresh control. A
// contact somebody else created was therefore invisible to every other open
// session until that person did a FULL browser reload (in-app navigation does
// not remount DataGate), and nothing on screen said the data was a snapshot.
// It reads exactly like a lost record. Reported repeatedly: Jason 2026-07-28
// ("changes not saved" — every write had in fact landed) and again 2026-08-26
// (Jeff Whitten / Tom Hayes "not in the list anywhere").
//
// The API already invalidates its own 60s dataset cache on every entities
// write (routes/entities.ts), so a refresh always sees the latest committed
// state — no cache-busting query param is needed here.

import { useStore } from './useStore'
import { datasetApi } from '@/api/client'

/** Refuse to run two refreshes at once — focus events fire in bursts. */
let inFlight: Promise<boolean> | null = null

/**
 * Pull the dataset and replace the store copy.
 *
 * Returns false without fetching when a write-through is still in flight:
 * `hydrate` REPLACES the whole dataset, so landing a refresh between an
 * optimistic local edit and its server write would visibly revert the row the
 * user just changed. The caller retries on the next tick/focus.
 */
export async function refreshDataset(): Promise<boolean> {
  if (inFlight) return inFlight
  if (useStore.getState().pendingWrites > 0) return false
  if (!useStore.getState().hydrated) return false // bootstrap owns the first load

  inFlight = (async () => {
    try {
      const data = await datasetApi.refresh()
      // Re-check: a write may have started while the request was in the air.
      if (useStore.getState().pendingWrites > 0) return false
      useStore.getState().hydrate(data)
      return true
    } catch (err) {
      // A failed background refresh is not a user-facing error — the store
      // keeps the copy it has and the chip keeps showing its real age.
      console.warn('[refreshDataset] failed:', err)
      return false
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}
